import React, { useState, useCallback, useEffect } from 'react';
import { Drawer } from '@mui/material';
import { 
  Close as CloseIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
  LocalShipping as RouteIcon,
  Star as DefaultIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useLayout } from '../../../context/LayoutContext';
import { useNotification } from '../../../context/NotificationContext';
import { useSchedule } from '../../../hooks/vehicle/useSchedule';
import { useVehicleSettingsOperations } from '../../../hooks/useVehicleSettingsOperations';
import PropTypes from 'prop-types';
import '../../../assets/scss/components/ui/drawner/vehicle/RouteDrawer.scss';
import { useOperationService } from '../../../service/documentationVehicleService';

const RouteDrawer = ({ 
  open, 
  onClose, 
  operationId, 
  operationName,
  onRouteSelect,
  selectedRouteId 
}) => {
  const { theme } = useLayout();
  const { showNotification } = useNotification();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [isReordering, setIsReordering] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  
  // Hooks para manejo de datos
  const { 
    useOperationRoutes, 
    assignRoute, 
    updateRoute, 
    removeRoute,
    isLoading 
  } = useSchedule(operationId);
  
  const { 
    useRoutes, 
    useRouteClients 
  } = useVehicleSettingsOperations();
  const operationService = useOperationService();
  
  // Queries de datos
  const { data: routesData, isLoading: isLoadingRoutes, refetch } = useOperationRoutes();
  const { data: allRoutesData, isLoading: isLoadingAllRoutes } = useRoutes();
  const { data: clientsData } = useRouteClients();
  
  const operationRoutes = routesData?.data || [];
  const allRoutes = allRoutesData?.data || [];
  const clients = clientsData?.data || [];

  // Filtrar rutas disponibles
  const availableRoutes = allRoutes.filter(route => 
    !operationRoutes.find(or => or.route_id === route.id) &&
    route.active &&
    (searchTerm ? route.name.toLowerCase().includes(searchTerm.toLowerCase()) : true) &&
    (clientFilter ? route.client === clientFilter : true)
  );

  // Gestión mejorada de tooltips
  const handleTooltipShow = useCallback((id) => {
    setActiveTooltip(id);
  }, []);

  const handleTooltipHide = useCallback(() => {
    setActiveTooltip(null);
  }, []);

  // Limpiar tooltip al cerrar drawer
  useEffect(() => {
    if (!open) {
      setActiveTooltip(null);
    }
  }, [open]);

  const handleAssignRoute = async (routeId, isDefault = false) => {
    try {
      await assignRoute({ routeId, isDefault });
      showNotification({
        message: 'Ruta asignada exitosamente',
        severity: 'success'
      });
      setIsAddDialogOpen(false);
      refetch();
    } catch (error) {
      showNotification({
        message: error.message || 'Error al asignar ruta',
        severity: 'error'
      });
    }
  };

  const handleToggleRouteStatus = async (relationId, currentStatus) => {
    try {
      await updateRoute({
        relationId,
        isActive: !currentStatus
      });
      showNotification({
        message: `Ruta ${!currentStatus ? 'activada' : 'desactivada'} exitosamente`,
        severity: 'success'
      });
      refetch();
    } catch (error) {
      showNotification({
        message: error.message || 'Error al actualizar ruta',
        severity: 'error'
      });
    }
  };

  const handleSetDefaultRoute = async (relationId) => {
    try {
      await updateRoute({
        relationId,
        isDefault: true
      });
      showNotification({
        message: 'Ruta establecida como predeterminada',
        severity: 'success'
      });
      refetch();
    } catch (error) {
      showNotification({
        message: error.message || 'Error al establecer ruta predeterminada',
        severity: 'error'
      });
    }
  };

  const handleRemoveRoute = async (relationId, routeName) => {
    if (window.confirm(`¿Estás seguro de eliminar la ruta "${routeName}" de esta operación?`)) {
      try {
        await removeRoute(relationId);
        showNotification({
          message: 'Ruta eliminada exitosamente',
          severity: 'success'
        });
        refetch();
      } catch (error) {
        showNotification({
          message: error.message || 'Error al eliminar ruta',
          severity: 'error'
        });
      }
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(operationRoutes);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setIsReordering(true);
    
    try {
      const routeOrders = items.map((item, index) => ({
        relationId: item.id,
        position: index
      }));

      await operationService.reorderOperationRoutes(operationId, routeOrders);
      
      showNotification({
        message: 'Orden actualizado exitosamente',
        severity: 'success'
      });
      refetch();
    } catch (error) {
      showNotification({
        message: error.message || 'Error al reordenar rutas',
        severity: 'error'
      });
    } finally {
      setIsReordering(false);
    }
  };

  const handleRouteClick = (route) => {
    if (onRouteSelect && route.is_active) {
      onRouteSelect(route.id, route);
      onClose();
    }
  };

  // Componentes personalizados mejorados
  const CustomChip = ({ label, icon: Icon, color = 'default', size = 'medium', variant = 'filled', className = '' }) => (
    <span className={`GM__${theme}-custom-chip ${color} ${size} ${variant} ${className}`}>
      {Icon && <Icon className={`GM__${theme}-chip-icon`} />}
      <span className={`GM__${theme}-chip-label`}>{label}</span>
    </span>
  );

  const CustomCircularProgress = ({ size = 40, className = '' }) => (
    <div 
      className={`GM__${theme}-circular-progress ${className}`}
      style={{ width: size, height: size }}
    >
      <div className={`GM__${theme}-spinner`}></div>
    </div>
  );

  // Tooltip mejorado con mejor gestión de eventos
  const CustomTooltip = ({ title, children, id }) => (
    <div 
      className={`GM__${theme}-tooltip-container`}
      onMouseEnter={() => handleTooltipShow(id)}
      onMouseLeave={handleTooltipHide}
      data-tooltip-active={activeTooltip === id}
    >
      {children}
      {activeTooltip === id && (
        <div className={`GM__${theme}-tooltip`}>
          {title}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        className={`GM__route-drawer-container-${theme}`}
        PaperProps={{
          className: `GM__${theme}-route-drawer-paper`,
        }}
      >
        <div className={`GM__${theme}-route-drawer`}>
          {/* Header */}
          <div className={`GM__${theme}-route-drawer-header`}>
            <div className={`GM__${theme}-drawer-title-container`}>
              <RouteIcon className={`GM__${theme}-drawer-icon`} />
              <h2 className={`GM__${theme}-drawer-title`}>
                Rutas - {operationName || 'Operación'}
              </h2>
            </div>
            <div className={`GM__${theme}-drawer-header-actions`}>
              <button
                onClick={() => setIsAddDialogOpen(true)}
                className={`GM__${theme}-add-button`}
                disabled={isLoading}
              >
                <AddIcon />
              </button>
              <button 
                onClick={onClose} 
                className={`GM__${theme}-close-button`}
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          <div className={`GM__${theme}-drawer-divider`}></div>

          {/* Contenido principal */}
          <div className={`GM__${theme}-route-drawer-content`}>
            {isLoadingRoutes ? (
              <div className={`GM__${theme}-loading-container`}>
                <CustomCircularProgress size={40} />
                <span>Cargando rutas...</span>
              </div>
            ) : operationRoutes.length === 0 ? (
              <div className={`GM__${theme}-empty-state`}>
                <RouteIcon className={`GM__${theme}-empty-icon`} />
                <h3 className={`GM__${theme}-empty-title`}>No hay rutas asignadas</h3>
                <p className={`GM__${theme}-empty-subtitle`}>
                  Haz clic en el botón + para asignar rutas a esta operación
                </p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="routes">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`GM__${theme}-route-list`}
                    >
                      {operationRoutes.map((route, index) => (
                        <Draggable
                          key={route.id}
                          draggableId={route.id}
                          index={index}
                          isDragDisabled={!route.is_active || isReordering}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`GM__${theme}-route-item ${
                                !route.is_active ? 'inactive' : ''
                              } ${snapshot.isDragging ? 'dragging' : ''} ${
                                selectedRouteId === route.id ? 'selected' : ''
                              }`}
                              onClick={() => handleRouteClick(route)}
                            >
                              <div 
                                {...provided.dragHandleProps}
                                className={`GM__${theme}-drag-handle`}
                              >
                                <DragIcon />
                              </div>
                              
                              <div className={`GM__${theme}-route-content`}>
                                <RouteIcon className={`GM__${theme}-route-icon`} />
                                
                                <div className={`GM__${theme}-route-info`}>
                                  <div className={`GM__${theme}-route-primary`}>
                                    <span className={`GM__${theme}-route-name`}>
                                      {route.route_name}
                                    </span>
                                    {route.is_default && (
                                      <CustomChip
                                        icon={DefaultIcon}
                                        label="Predeterminada"
                                        size="small"
                                        color="primary"
                                        className={`GM__${theme}-default-chip`}
                                      />
                                    )}
                                  </div>
                                  
                                  <div className={`GM__${theme}-route-secondary`}>
                                    <span className={`GM__${theme}-route-path`}>
                                      {route.loading_point_name} → {route.unloading_point_name}
                                    </span>
                                    <div className={`GM__${theme}-route-meta`}>
                                      <span className={`GM__${theme}-route-distance`}>
                                        {route.distance_km} km
                                      </span>
                                      <CustomChip
                                        label={route.client}
                                        size="small"
                                        variant="outlined"
                                        className={`GM__${theme}-client-chip`}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className={`GM__${theme}-route-actions`}>
                                {!route.is_default && route.is_active && (
                                  <CustomTooltip title="Establecer como predeterminada" id={`default-${route.id}`}>
                                    <button
                                      type="button"
                                      className={`GM__${theme}-action-button default`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSetDefaultRoute(route.id);
                                      }}
                                    >
                                      <DefaultIcon />
                                    </button>
                                  </CustomTooltip>
                                )}
                                
                                <CustomTooltip title={route.is_active ? "Desactivar" : "Activar"} id={`toggle-${route.id}`}>
                                  <label className={`GM__${theme}-switch-container`}>
                                    <input
                                      type="checkbox"
                                      checked={route.is_active}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleToggleRouteStatus(route.id, route.is_active);
                                      }}
                                      disabled={isLoading}
                                      className={`GM__${theme}-switch-input`}
                                    />
                                    <span className={`GM__${theme}-switch-slider`}></span>
                                  </label>
                                </CustomTooltip>
                                
                                <CustomTooltip title="Eliminar" id={`delete-${route.id}`}>
                                  <button
                                    type="button"
                                    className={`GM__${theme}-action-button delete`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveRoute(route.id, route.route_name);
                                    }}
                                    disabled={route.is_default || isLoading}
                                  >
                                    <DeleteIcon />
                                  </button>
                                </CustomTooltip>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </Drawer>

      {/* Modal rediseñado para asignar rutas */}
      {isAddDialogOpen && (
        <div 
          className={`GM__${theme}-modal-backdrop`}
          onClick={() => setIsAddDialogOpen(false)}
        >
          <div 
            className={`GM__${theme}-modal-container`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal */}
            <div className={`GM__${theme}-modal-header`}>
              <div className={`GM__${theme}-modal-header-content`}>
                <RouteIcon className={`GM__${theme}-modal-icon`} />
                <div className={`GM__${theme}-modal-title-section`}>
                  <h2 className={`GM__${theme}-modal-title`}>
                    Asignar Rutas
                  </h2>
                  <p className={`GM__${theme}-modal-subtitle`}>
                    Selecciona las rutas para {operationName}
                  </p>
                </div>
              </div>
              <button 
                type="button"
                className={`GM__${theme}-modal-close-btn`}
                onClick={() => setIsAddDialogOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            
            {/* Cuerpo del modal */}
            <div className={`GM__${theme}-modal-body`}>
              {/* Sección de filtros mejorada */}
              <div className={`GM__${theme}-filters-section`}>
                <div className={`GM__${theme}-search-field`}>
                  <div className={`GM__${theme}-search-input-wrapper`}>
                    <SearchIcon className={`GM__${theme}-search-icon`} />
                    <input
                      type="text"
                      placeholder="Buscar ruta por nombre..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`GM__${theme}-search-input`}
                    />
                  </div>
                </div>
                
                <div className={`GM__${theme}-filter-field`}>
                  <select
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    className={`GM__${theme}-filter-select`}
                  >
                    <option value="">Todos los clientes</option>
                    {clients.map(client => (
                      <option key={client} value={client}>
                        {client}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Separador visual */}
              <div className={`GM__${theme}-section-divider`}></div>

              {/* Lista de rutas disponibles rediseñada */}
              <div className={`GM__${theme}-routes-section`}>
                {isLoadingAllRoutes ? (
                  <div className={`GM__${theme}-modal-loading-state`}>
                    <CustomCircularProgress size={32} />
                    <span className={`GM__${theme}-loading-text`}>
                      Cargando rutas disponibles...
                    </span>
                  </div>
                ) : availableRoutes.length === 0 ? (
                  <div className={`GM__${theme}-modal-empty-state`}>
                    <RouteIcon className={`GM__${theme}-empty-state-icon`} />
                    <h3 className={`GM__${theme}-empty-state-title`}>
                      No hay rutas disponibles
                    </h3>
                    <p className={`GM__${theme}-empty-state-message`}>
                      {searchTerm || clientFilter 
                        ? 'No se encontraron rutas que coincidan con los filtros aplicados'
                        : 'Todas las rutas ya están asignadas a esta operación'
                      }
                    </p>
                  </div>
                ) : (
                  <div className={`GM__${theme}-routes-grid`}>
                    <div className={`GM__${theme}-routes-header`}>
                      <span className={`GM__${theme}-routes-count`}>
                        {availableRoutes.length} ruta{availableRoutes.length !== 1 ? 's' : ''} disponible{availableRoutes.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <div className={`GM__${theme}-routes-list`}>
                      {availableRoutes.map(route => (
                        <div
                          key={route.id}
                          className={`GM__${theme}-route-card`}
                          onClick={() => handleAssignRoute(route.id)}
                        >
                          <div className={`GM__${theme}-route-card-header`}>
                            <RouteIcon className={`GM__${theme}-route-card-icon`} />
                            <div className={`GM__${theme}-route-card-title`}>
                              <h4 className={`GM__${theme}-route-card-name`}>
                                {route.name}
                              </h4>
                              <CustomChip
                                label={route.client}
                                size="small"
                                variant="outlined"
                                className={`GM__${theme}-route-card-client`}
                              />
                            </div>
                          </div>
                          
                          <div className={`GM__${theme}-route-card-body`}>
                            <div className={`GM__${theme}-route-card-path`}>
                              <span className={`GM__${theme}-route-origin`}>
                                {route.loading_point_name}
                              </span>
                              <span className={`GM__${theme}-route-arrow`}>→</span>
                              <span className={`GM__${theme}-route-destination`}>
                                {route.unloading_point_name}
                              </span>
                            </div>
                            
                            <div className={`GM__${theme}-route-card-meta`}>
                              <span className={`GM__${theme}-route-distance-badge`}>
                                {route.distance_km} km
                              </span>
                            </div>
                          </div>
                          
                          <div className={`GM__${theme}-route-card-footer`}>
                            <span className={`GM__${theme}-route-card-action`}>
                              Haz clic para asignar
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer del modal */}
            <div className={`GM__${theme}-modal-footer`}>
              <button 
                type="button"
                className={`GM__${theme}-modal-cancel-btn`}
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

RouteDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  operationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  operationName: PropTypes.string,
  onRouteSelect: PropTypes.func,
  selectedRouteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default RouteDrawer;